/*
 * Copyright (c) 2022. Revo Digital
 * ---
 * Author: gabriele
 * File: ObservableTable.ts
 * Project: mle-client-n
 * Committed last: 2022/1/14 @ 113
 * ---
 * Description:
 */

import {
  defaultSetterOptions,
  ObservableSetterOptions,
  ObservableShape
}                                                       from './ObservableShape';
import Pamela
                                                        from '@revodigital/pamela';
import { action, computed, makeObservable, observable } from 'mobx';
import {
  HorizontalAlignment
}                                                       from '@revodigital/pamela/lib/configuration/Alignment';
import {
  CHANGED
}                                                       from '@revodigital/pamela/lib/events/text/Changed';
import { Editor }                                       from './Editor';
import KonvaEventObject = Pamela.KonvaEventObject;

export class ObservableText extends ObservableShape<Pamela.Text> {
  @observable
  private _text: string;
  @observable
  private _lockSize: boolean;
  @observable
  private _fontSize: number;
  @observable
  private _fontFamily: string;
  @observable
  private _margin: number;
  @observable
  private _fontStyle: string;
  @observable
  private _textDecoration: string;
  @observable
  private _backgroundColor: string;
  @observable
  private _hAlign: HorizontalAlignment;
  @observable
  private _lineHeight: number;

  constructor(shape: Pamela.Text, editor: Editor) {
    super(shape, editor);
    this._text = shape.text();
    this._lockSize = shape.lockSize();
    this._fontFamily = shape.fontFamily();
    this._fontSize = shape.fontSize();
    this._margin = shape.padding();
    this._fontStyle = shape.fontStyle();
    this._textDecoration = shape.textDecoration();
    this._backgroundColor = shape.backgroundColor();
    this._hAlign = shape.align();
    this._lineHeight = shape.lineHeight();

    // Attach event listener
    editor.pamelaStage__nonObservable.on(CHANGED,
      (event: KonvaEventObject<Pamela.Text>) => {
        const node = (event as any).node as Pamela.Shape;
        if (node.className === Pamela.Text.prototype.className) {
          const text = node as Pamela.Text;
          if (text.fontSize() !== this._fontSize) this._fontSize = text.fontSize();
        }
      });
    makeObservable(this);
  }

  @action
  public setBackgroundColor(value: string, options: ObservableSetterOptions = defaultSetterOptions) {
    this._backgroundColor = value;

    if (options.applyToPamelaShape) this.pamelaShape__nonObservable.backgroundColor(
      value);
  }

  @action
  public setLineHeight(value: number, options: ObservableSetterOptions = defaultSetterOptions) {
    this._lineHeight = value;

    if (options.applyToPamelaShape) this.pamelaShape__nonObservable.lineHeight(
      value);
  }

  @action
  public setHorizontalAlignment(value: HorizontalAlignment, options: ObservableSetterOptions = defaultSetterOptions) {
    this._hAlign = value;

    if (options.applyToPamelaShape) this.pamelaShape__nonObservable.align(value);
  }

  @action
  public setFontSize(value: number, options: ObservableSetterOptions = defaultSetterOptions) {
    this._fontSize = value;

    if (options.applyToPamelaShape) this.pamelaShape__nonObservable.fontSize(
      value);
  }

  @action
  public setMargin(value: number, options: ObservableSetterOptions = defaultSetterOptions) {
    this._margin = value;

    if (options.applyToPamelaShape) {
      this.pamelaShape__nonObservable.padding(
        value);
      //this.pamelaShape__nonObservable.resize();
      //this.pamelaShape__nonObservable.draw();
    }
  }

  @action
  public setFontStyle(value: string, options: ObservableSetterOptions = defaultSetterOptions) {
    this._fontStyle = value;

    if (options.applyToPamelaShape) this.pamelaShape__nonObservable.fontStyle(
      value);
  }

  @action
  public setTextDecoration(value: string, options: ObservableSetterOptions = defaultSetterOptions) {
    this._textDecoration = value;

    if (options.applyToPamelaShape) this.pamelaShape__nonObservable.textDecoration(
      value);
  }

  @action
  public setFontFamily(value: string, options: ObservableSetterOptions = defaultSetterOptions) {
    this._fontFamily = value;

    if (options.applyToPamelaShape) this.pamelaShape__nonObservable.fontFamily(
      value);
  }

  @action
  public setText(value: string, options: ObservableSetterOptions = defaultSetterOptions) {
    this._text = value;

    if (options.applyToPamelaShape) {
      this.pamelaShape__nonObservable.text(value);
      if (this.pamelaShape__nonObservable.lockSize()) this.pamelaShape__nonObservable.fitContainer();
      else this.pamelaShape__nonObservable.resize();
    }
  }

  @action
  public setLockSize(value: boolean, options: ObservableSetterOptions = defaultSetterOptions) {
    this._lockSize = value;

    if (options.applyToPamelaShape) {
      this.pamelaShape__nonObservable.lockSize(
        value);
      this.pamelaShape__nonObservable.fitContainer();
    }
  }

  @computed
  public get lockSize() {
    return this._lockSize;
  }

  @computed
  public get text() {
    return this._text;
  }

  @computed
  public get fontSize() {
    return this._fontSize;
  }

  @computed
  public get fontFamily() {
    return this._fontFamily;
  }

  @computed
  public get textDecoration() {
    return this._textDecoration;
  }

  @computed
  public get lineHeight() {
    return this._lineHeight;
  }

  @computed
  public get backgroundColor() {
    return this._backgroundColor;
  }

  @computed
  public get fontStyle() {
    return this._fontStyle;
  }

  @computed
  public get horizontalAlignment() {
    return this._hAlign;
  }

  @computed
  public get margin() {
    return this._margin;
  }
}